import { useState } from "react"
import { toast } from "react-toastify"

import { useLazyGetMonthlyEngagementSummaryQuery } from "store/api/engagementApi"
import { useAppSelector } from "store/hooks"
import { contractorThemeSelector, userLogoSelector } from "store/tempDataSlise"
import { ContractorThemes } from "constants/ContractorThemes"
import SubscriptionPlan from "types/enums/SubscriptionPlan"

import { isValidDateRange } from "utils/dateUtils"
import { triggerFileDownload } from "utils/triggerFileDownload"
import { buildMonthlyEngagementReportData } from "pages/contractor/MonthlyEngagementPage/reportData"

type MonthlyEngagementDownloadParams = {
  contractor_id: number
  reporting_period_from: string
  reporting_period_to: string
}

export const useMonthlyEngagementPdfDownloader = () => {
  const userProfile = useAppSelector(state => state.user.userProfile)
  const contractor = userProfile?.contractor
  const currentTheme = useAppSelector(contractorThemeSelector)
  const userLogo = useAppSelector(userLogoSelector)
  const [getMonthlyEngagementSummary] = useLazyGetMonthlyEngagementSummaryQuery()
  const [isDownloading, setIsDownloading] = useState(false)
  const canUseCustomTheme =
    contractor?.subscription_plan === SubscriptionPlan.E_PLUS || Boolean(userProfile?.enterprise)
  const backgroundGradient = canUseCustomTheme
    ? currentTheme.backgroundGradient
    : ContractorThemes.default.backgroundGradient

  const downloadMonthlyEngagementPdf = async (params: MonthlyEngagementDownloadParams) => {
    if (isDownloading) return

    if (!isValidDateRange(params.reporting_period_from, params.reporting_period_to)) {
      toast.error("Invalid reporting period")
      return
    }

    setIsDownloading(true)
    try {
      const summaryResult = await getMonthlyEngagementSummary(params).unwrap()
      const report = buildMonthlyEngagementReportData({ summary: summaryResult })
      const { generateMonthlyEngagementPdf } =
        await import("pages/contractor/MonthlyEngagementPage/pdf/generateMonthlyEngagementPdf")
      const blob = await generateMonthlyEngagementPdf(report, backgroundGradient, userLogo)
      triggerFileDownload(blob, "monthly-engagement-report.pdf")
    } catch (err) {
      console.error("Monthly engagement PDF generation failed:", err)
      toast.error("PDF download failed. Please try again later.")
    } finally {
      setIsDownloading(false)
    }
  }

  return {
    downloadMonthlyEngagementPdf,
    isDownloadingMonthlyEngagementPdf: isDownloading,
  }
}
